import React, { useEffect, useRef, useState } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import PathPickSectionCard from "@/features/pathpick/components/ui/PathPickSectionCard";
// 핫딜 상품/인기 다운로드 상품 탭 주석 처리로 미사용 — 복원 시 함께 해제
// import PathPickProductCard from "@/features/pathpick/components/product/PathPickProductCard";
import PathPickSearchProductsPopularLineSheetList from "@/features/pathpick/features/search-products/components/popular/PathPickSearchProductsPopularLineSheetList";
import PathPickSearchProductsPopularBrandCard from "@/features/pathpick/features/search-products/components/popular/PathPickSearchProductsPopularBrandCard";
import PathPickFavoriteBrandsModal from "@/features/pathpick/features/search-products/components/popular/PathPickFavoriteBrandsModal";
// import PathPickSizeStockModal from "@/features/pathpick/components/product/PathPickSizeStockModal";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useGetPathPickPopularBrands from "@/features/pathpick/features/search-products/hooks/query/useGetPathPickPopularBrands";
import useGetFavoriteBrands from "@/shared/hooks/favorite-brands/useGetFavoriteBrands";
import useToggleFavoriteBrand from "@/features/pathpick/features/search-products/hooks/useToggleFavoriteBrand";
import usePathPickProductFilter from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import usePathPickFilterableBrandIds from "@/features/pathpick/features/search-products/hooks/usePathPickFilterableBrandIds";
import {
  HOT_DEAL_LINE_SHEETS,
  // HOT_DEAL_PRODUCTS,
  // POPULAR_DOWNLOAD_PRODUCTS,
} from "@/features/pathpick/constants";
import {
  PathPickHotDealLineSheet,
  // PathPickPopularProduct,
} from "@/features/pathpick/types";

type PopularTab =
  | "hotDeal"
  | "lineSheet"
  | "download"
  | "brand"
  | "favoriteBrand";

// 인기/즐겨찾기 두 응답을 같은 카드 그리드에 태우기 위한 표시용 형태
interface BrandCardItem {
  brandId: number;
  brandName: string;
  logoUrl?: string | null;
}

// const DOWNLOAD_PAGE_SIZE = 10;

interface Props {
  selectedLineSheetId: number | null;
  onSelectLineSheet: (lineSheet: PathPickHotDealLineSheet) => void;
}

function PathPickSearchProductsPopularSection({
  selectedLineSheetId,
  onSelectLineSheet,
}: Props) {
  const { t } = useI18nHelper();
  const { pathPickPopularBrands } = useGetPathPickPopularBrands({ limit: 20 });
  const { favoriteBrands } = useGetFavoriteBrands();
  // 같은 쿼리를 공유하므로 추가 요청은 없다.
  const { isFavorite, toggle: toggleFavoriteBrand } = useToggleFavoriteBrand();
  const { filter, apply } = usePathPickProductFilter();
  // 브랜드 필터 옵션에 없는 브랜드는 걸어도 해제할 수 없어 카드에서 막는다.
  const filterableBrandIds = usePathPickFilterableBrandIds();
  const [activeTab, setActiveTab] = useState<PopularTab>("brand");
  const [isFavoriteBrandsModalOpen, setIsFavoriteBrandsModalOpen] =
    useState<boolean>(false);
  // 핫딜 상품/인기 다운로드 상품 탭 주석 처리로 미사용 — 복원 시 함께 해제
  // const [sizeStockItem, setSizeStockItem] =
  //   useState<PathPickPopularProduct | null>(null);
  // const [visibleDownloadCount, setVisibleDownloadCount] =
  //   useState<number>(DOWNLOAD_PAGE_SIZE);
  // 브랜드 리스트는 접힘(한 줄)/펼침 토글 — 한 줄 기준은 개수가 아니라 그리드 레이아웃(CSS)로 강제한다.
  const [isBrandListExpanded, setIsBrandListExpanded] =
    useState<boolean>(false);
  const brandGridRef = useRef<HTMLDivElement>(null);
  // 접힘 상태에서 실제로 가려진 카드가 있는지(=더보기 노출 여부)는 개수로 알 수 없어 측정한다.
  const [isBrandListOverflowing, setIsBrandListOverflowing] =
    useState<boolean>(false);

  const isBrandTab = activeTab === "brand" || activeTab === "favoriteBrand";

  const brandCards: BrandCardItem[] =
    activeTab === "favoriteBrand"
      ? favoriteBrands.brands.map((brand) => ({
          brandId: brand.id,
          brandName: brand.name,
          logoUrl: brand.logoUrl,
        }))
      : pathPickPopularBrands.brands.map((brand) => ({
          brandId: brand.brandId,
          brandName: brand.brandName,
          logoUrl: brand.logoUrl,
        }));

  useEffect(() => {
    const el = brandGridRef.current;
    if (!el) {
      return;
    }
    const check = () =>
      setIsBrandListOverflowing(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
    // 브랜드 탭이 아닐 때는 그리드가 언마운트되므로 탭 복귀 시 재측정한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandCards.length, activeTab]);

  // 탭마다 카드 수가 달라 접힘 상태를 이어받으면 더보기 노출이 어긋난다.
  const handleChangeTab = (tab: PopularTab) => {
    setActiveTab(tab);
    setIsBrandListExpanded(false);
  };

  // 카드 클릭은 브랜드 필터 토글 — 선택된 카드를 다시 누르면 그 브랜드만 빠진다.
  const handleToggleBrandFilter = (brand: BrandCardItem) => {
    // 카드가 비활성이라 눌릴 일이 없지만, 필터에 없는 브랜드가 URL에 실리는 경로를 아예 막는다.
    if (!filterableBrandIds.has(brand.brandId)) {
      return;
    }
    const current = filter.brandId__in ?? [];
    const next = current.includes(brand.brandId)
      ? current.filter((id) => id !== brand.brandId)
      : [...current, brand.brandId];
    apply({ ...filter, brandId__in: next });
  };

  // 브랜드 필터에 걸린 브랜드는 모두 선택 상태로 본다 — 여러 개가 한 번에 걸릴 수 있다.
  const isBrandSelected = (brandId: number) =>
    filter.brandId__in?.includes(brandId) ?? false;

  // 섹션 제목이 없어 탭 라벨이 제목 역할을 한다 — 설명은 탭 하단에 탭별로 노출한다.
  const tabs: {
    id: PopularTab;
    label: string;
    count?: number;
    description: string;
  }[] = [
    // {
    //   id: "hotDeal",
    //   label: t("path_pick:search_products.popular.tab_hot_deal"),
    // },
    // {
    //   id: "lineSheet",
    //   label: `🔥 ${t(
    //     "path_pick:search_products.popular.tab_hot_deal_line_sheet"
    //   )}`,
    // },
    // {
    //   id: "download",
    //   label: t("path_pick:search_products.popular.tab_popular_download"),
    // },
    {
      id: "brand",
      label: t("path_pick:search_products.popular.tab_popular_brand"),
      description: t("path_pick:search_products.popular.description"),
    },
    {
      id: "favoriteBrand",
      label: t("path_pick:search_products.popular.tab_favorite_brand"),
      count: favoriteBrands.brands.length,
      description: t(
        "path_pick:search_products.popular.favorite_brand_description"
      ),
    },
  ];

  const activeTabDescription = tabs.find(
    (tab) => tab.id === activeTab
  )?.description;

  const manageFavoriteBrandsButton = (
    <AppButton
      variant={"white"}
      onClick={() => setIsFavoriteBrandsModalOpen(true)}
    >
      <Flex gap={"4px"} alignItems={"center"}>
        <CustomIcon src={"star"} width={16} height={16} color={"#444440"} />
        <Text textStyle={"body-sm-bold"}>
          {t("path_pick:search_products.popular.manage_favorite_brands")}
        </Text>
      </Flex>
    </AppButton>
  );

  return (
    <>
      <PathPickSectionCard
        header={
          <Flex
            justifyContent={"space-between"}
            alignItems={"flex-start"}
            gap={"12px"}
          >
            <Flex flexDirection={"column"} gap={"6px"} flex={1} minWidth={0}>
              {/* 관리 버튼은 탭과 같은 wrap 줄에 두면 폭이 모자랄 때 혼자 다음 줄로
                  떨어지므로, 탭 묶음과 칸을 나눠 항상 첫 줄 오른쪽 끝에 둔다. */}
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"8px"}
              >
                <Flex gap={"8px"} flexWrap={"wrap"}>
                  {tabs.map((tab) => (
                    <AppButton
                      key={tab.id}
                      variant={"white"}
                      isActive={activeTab === tab.id}
                      onClick={() => handleChangeTab(tab.id)}
                    >
                      <Text textStyle={"body-sm-bold"}>
                        {tab.count === undefined
                          ? tab.label
                          : `${tab.label} ${tab.count}`}
                      </Text>
                    </AppButton>
                  ))}
                </Flex>
                {/* 좁은 폭에서는 관리 버튼을 짧은 ghost 버튼으로 둔다. AppButton은
                    Tooltip으로 감싸여 있어 display 분기는 바깥 Flex에 건다. */}
                <Flex display={{ base: "flex", md: "none" }} flexShrink={0}>
                  <AppButton
                    variant={"ghost"}
                    onClick={() => setIsFavoriteBrandsModalOpen(true)}
                    chakraProps={{
                      // medium 기본 좌우 패딩(32px)이면 버튼이 탭만큼 넓어진다.
                      padding: "4px 8px",
                      "aria-label": t(
                        "path_pick:search_products.popular.manage_favorite_brands"
                      ),
                    }}
                  >
                    <Flex gap={"4px"} alignItems={"center"}>
                      <CustomIcon src={"star"} width={16} height={16} />
                      <Text textStyle={"body-sm-bold"}>
                        {t(
                          "path_pick:search_products.popular.manage_favorite_brands_short"
                        )}
                      </Text>
                    </Flex>
                  </AppButton>
                </Flex>
              </Flex>
              <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                {activeTabDescription}
              </Text>
            </Flex>
            <Flex display={{ base: "none", md: "flex" }}>
              {manageFavoriteBrandsButton}
            </Flex>
          </Flex>
        }
      >
        <Flex flexDirection={"column"} gap={"12px"}>
          {/*{activeTab === "hotDeal" && (*/}
          {/*  <Flex gap={"12px"} overflowX={"auto"} paddingBottom={"4px"}>*/}
          {/*    {HOT_DEAL_PRODUCTS.map((item) => (*/}
          {/*      <Flex key={item.id} width={"200px"} flexShrink={0}>*/}
          {/*        <PathPickProductCard*/}
          {/*          item={item}*/}
          {/*          kreamBidCount={item.kreamBidCount}*/}
          {/*          expectedMargin={item.expectedMargin}*/}
          {/*          marginRate={item.marginRate}*/}
          {/*          isPicked={item.isInBasket}*/}
          {/*          isDownloaded={item.isDownloaded}*/}
          {/*          onOpenSizeStock={() => setSizeStockItem(item)}*/}
          {/*        />*/}
          {/*      </Flex>*/}
          {/*    ))}*/}
          {/*  </Flex>*/}
          {/*)}*/}

          {activeTab === "lineSheet" && (
            <PathPickSearchProductsPopularLineSheetList
              items={HOT_DEAL_LINE_SHEETS}
              selectedLineSheetId={selectedLineSheetId}
              onSelectLineSheet={onSelectLineSheet}
            />
          )}

          {/*{activeTab === "download" && (*/}
          {/*  <Flex flexDirection={"column"} gap={"12px"}>*/}
          {/*    <Grid*/}
          {/*      templateColumns={*/}
          {/*        // 최대 8열: 각 열의 최소 너비를 (전체 - gap 7개)/8로 강제해 9열째가 못 들어오게 한다.*/}
          {/*        "repeat(auto-fit, minmax(max(180px, (100% - 84px) / 8), 1fr))"*/}
          {/*      }*/}
          {/*      gap={"12px"}*/}
          {/*    >*/}
          {/*      {POPULAR_DOWNLOAD_PRODUCTS.slice(0, visibleDownloadCount).map(*/}
          {/*        (item) => (*/}
          {/*          <PathPickProductCard*/}
          {/*            key={item.id}*/}
          {/*            item={item}*/}
          {/*            kreamBidCount={item.kreamBidCount}*/}
          {/*            expectedMargin={item.expectedMargin}*/}
          {/*            marginRate={item.marginRate}*/}
          {/*            isPicked={item.isInBasket}*/}
          {/*            isDownloaded={item.isDownloaded}*/}
          {/*            onOpenSizeStock={() => setSizeStockItem(item)}*/}
          {/*          />*/}
          {/*        )*/}
          {/*      )}*/}
          {/*    </Grid>*/}
          {/*    {visibleDownloadCount < POPULAR_DOWNLOAD_PRODUCTS.length && (*/}
          {/*      <Flex justifyContent={"center"}>*/}
          {/*        <AppButton*/}
          {/*          variant={"white"}*/}
          {/*          onClick={() =>*/}
          {/*            setVisibleDownloadCount(*/}
          {/*              (prev) => prev + DOWNLOAD_PAGE_SIZE*/}
          {/*            )*/}
          {/*          }*/}
          {/*        >*/}
          {/*          <Text textStyle={"body-sm-bold"}>*/}
          {/*            {t("path_pick:search_products.popular.load_more")}*/}
          {/*          </Text>*/}
          {/*        </AppButton>*/}
          {/*      </Flex>*/}
          {/*    )}*/}
          {/*  </Flex>*/}
          {/*)}*/}

          {activeTab === "favoriteBrand" && brandCards.length === 0 && (
            <Flex
              flexDirection={"column"}
              alignItems={"center"}
              gap={"8px"}
              padding={"24px 0"}
            >
              <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                {t("path_pick:search_products.popular.favorite_brand_empty")}
              </Text>
              {manageFavoriteBrandsButton}
            </Flex>
          )}

          {isBrandTab && brandCards.length > 0 && (
            <Flex flexDirection={"column"} gap={"12px"}>
              <Grid
                ref={brandGridRef}
                templateColumns={"repeat(auto-fill, minmax(220px, 1fr))"}
                gap={"12px"}
                // 접힘: 첫 행은 명시적 auto 트랙, 2행부터는 암시적 행(autoRows 0) + rowGap 0
                // + overflow hidden → 화면 폭과 무관하게 항상 정확히 한 줄만 보인다.
                // (templateRows가 없으면 첫 행도 암시적 행이라 autoRows 0이 적용돼 전부 숨는다)
                templateRows={isBrandListExpanded ? undefined : "auto"}
                autoRows={isBrandListExpanded ? undefined : "0"}
                rowGap={isBrandListExpanded ? "12px" : "0"}
                overflow={"hidden"}
              >
                {brandCards.map((brand, index) => (
                  <PathPickSearchProductsPopularBrandCard
                    key={brand.brandId}
                    brandName={brand.brandName}
                    logoUrl={brand.logoUrl}
                    rank={index + 1}
                    isSelected={isBrandSelected(brand.brandId)}
                    isFilterable={filterableBrandIds.has(brand.brandId)}
                    isFavorite={isFavorite(brand.brandId)}
                    onToggleFilter={() => handleToggleBrandFilter(brand)}
                    onToggleFavorite={() =>
                      toggleFavoriteBrand(brand.brandId, brand.brandName)
                    }
                  />
                ))}
              </Grid>
              {(isBrandListExpanded || isBrandListOverflowing) && (
                <Flex justifyContent={"center"}>
                  <AppButton
                    variant={"white"}
                    onClick={() => setIsBrandListExpanded((prev) => !prev)}
                  >
                    <Text textStyle={"body-sm-bold"}>
                      {t(
                        isBrandListExpanded
                          ? "path_pick:search_products.popular.collapse"
                          : "path_pick:search_products.popular.load_more"
                      )}
                    </Text>
                  </AppButton>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </PathPickSectionCard>
      {/*{sizeStockItem && (*/}
      {/*  <PathPickSizeStockModal*/}
      {/*    isOpen*/}
      {/*    onClose={() => setSizeStockItem(null)}*/}
      {/*    item={sizeStockItem}*/}
      {/*  />*/}
      {/*)}*/}
      {/* 조건부 마운트로 열 때마다 draft가 서버 값으로 새로 잡히게 한다. */}
      {isFavoriteBrandsModalOpen && (
        <PathPickFavoriteBrandsModal
          isOpen
          onClose={() => setIsFavoriteBrandsModalOpen(false)}
        />
      )}
    </>
  );
}

export default PathPickSearchProductsPopularSection;
